/**
 * changeWords - a changed step field in a person's words, for the agent's
 * list of changes, the reasons a confirmation gives and the chart's list of
 * changes alike.
 *
 * Values are always named, before and after: prose cut to a short line
 * around where the two differ, lists by their names, a Condition's check as
 * what it does rather than its stored value. A cut shows as an ellipsis
 * outside the quoted text, so a confirmation that escapes what it quotes
 * still shows it as one.
 *
 * @module changeWords
 */

const SHORT = 60;
/** How much two values' shared opening is kept before where they first differ. */
const LEAD = 20;
const LISTED = 4;

const NOUNS: Readonly<Record<string, string>> = {
  instructions: "instructions",
  recipientEmails: "recipients",
  approverEmails: "approvers",
  spaceId: "space",
  sources: "sources",
  taskId: "browser task",
  inputs: "inputs",
  inputHint: "Run question",
  mode: "check",
  question: "question",
  model: "model",
  fields: "fields",
  template: "template",
  shape: "shape",
  assigneeEmails: "people who do it",
  source: "table",
  maxRows: "most rows",
  left: "first table",
  right: "second table",
  key: "match column",
  rightKey: "second table's match column",
  rules: "rules",
  missing: "check for rows only one table has",
  format: "format",
  files: "files asked for at Run",
  attachments: "attached files",
  attachmentName: "attachment name",
  effort: "effort",
};

const CHECKS: Readonly<Record<string, string>> = { ask: "asking a question", contains: "looking for text" };

const FORMATS: Readonly<Record<string, string>> = { markdown: "a document", excel: "an Excel workbook", word: "a Word document" };

const EMPTY: Readonly<Record<string, string>> = {
  recipientEmails: "nobody",
  approverEmails: "nobody",
  assigneeEmails: "whoever runs it",
  rules: "no rules",
  yes: "nowhere",
  no: "nowhere",
  fields: "no fields",
};

/** A step field as a person names it: `type` its kind, `label` its name, `config.<key>` its setting. */
export function stepFieldNoun(field: string): string {
  if (field === "type") return "kind";
  if (field === "label") return "name";
  const key = field.startsWith("config.") ? field.slice("config.".length) : field;
  return NOUNS[key] ?? key.replace(/([a-z])([A-Z])/gu, "$1 $2").toLowerCase();
}

/** "the question of “Check”". */
function fieldPhrase(field: string, step: string): string {
  if (field === "text") return `the text ${step} looks for`;
  if (field === "yes" || field === "no") return `where ${step} leads on ${field}`;
  return `the ${NOUNS[field] ?? field} of ${step}`;
}

const oneLine = (text: string) => text.replace(/\s+/gu, " ").trim();

/**
 * Up to a short line of `line` from `start`, cut at a word but never before
 * `through`, so two values cut at the same place still differ; an ellipsis
 * outside the quote marks each side cut away.
 */
const excerpt = (line: string, quote: (text: string) => string, start = 0, through = start): string => {
  let end = line.length;
  if (end - start > SHORT) {
    const cut = Math.min(line.length, Math.max(start + SHORT - 1, through + 1));
    const space = line.lastIndexOf(" ", cut);
    end = space > Math.max(start + SHORT / 2, through) ? space : cut;
  }
  return `${start > 0 ? "…" : ""}${quote(line.slice(start, end).trim())}${end < line.length ? "…" : ""}`;
};

/** Where two values' excerpts start (at a word a little before their first difference) and the first difference itself. */
function differenceAt(before: string, after: string): { readonly start: number; readonly through: number } {
  let shared = 0;
  while (shared < before.length && before[shared] === after[shared]) shared += 1;
  if (shared <= SHORT - LEAD) return { start: 0, through: shared };
  const space = before.lastIndexOf(" ", shared - LEAD);
  return { start: space < shared - LEAD - SHORT / 2 ? shared - LEAD : space + 1, through: shared };
}

const nameOf = (item: unknown): string | null =>
  typeof item === "string" ? item : typeof item === "object" && item !== null && typeof (item as { name?: unknown }).name === "string" ? (item as { name: string }).name : null;

/** A field's value in words, or null when it has none a person could read (a list of sources). */
export function valueWords(field: string, value: unknown, quote: (text: string) => string, start = 0, through = start): string | null {
  if (field === "mode" && typeof value === "string") return CHECKS[value] ?? quote(value);
  if (field === "format") return typeof value === "string" ? (FORMATS[value] ?? quote(value)) : "a document";
  if (value === undefined || value === null || value === "") return "nothing";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "string") return excerpt(oneLine(value), quote, start, through);
  if (!Array.isArray(value)) return null;
  if (value.length === 0) return EMPTY[field] ?? "nothing";
  const names = value.map(nameOf);
  if (names.some((name) => name === null)) return null;
  const shown = (names as ReadonlyArray<string>).slice(0, LISTED).map((name) => excerpt(oneLine(name), quote));
  return value.length > LISTED ? `${shown.join(", ")} and ${value.length - LISTED} more` : shown.join(", ");
}

const onlySpacingDiffers = (before: unknown, after: unknown): boolean =>
  typeof before === "string" && typeof after === "string" && before !== after && oneLine(before) === oneLine(after);

/** "the question of “Check” from “a” to “b”"; the values are left out only when they have no words. */
export function fieldChange(field: string, step: string, before: unknown, after: unknown, quote: (text: string) => string): string {
  if (onlySpacingDiffers(before, after)) return `only the line breaks or spacing of ${fieldPhrase(field, step)}`;
  const { start, through } = typeof before === "string" && typeof after === "string" ? differenceAt(oneLine(before), oneLine(after)) : { start: 0, through: 0 };
  const was = valueWords(field, before, quote, start, through);
  const now = valueWords(field, after, quote, start, through);
  return `${fieldPhrase(field, step)}${was !== null && now !== null ? ` from ${was} to ${now}` : ""}`;
}
